import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

const bodyScrollLockOwners = new Set();
let bodyScrollLockState = null;

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    this.#closeOperation += 1;
    this.#cancelPendingOpen();
    this.#removeInteractionListeners();
    this.#handleResize.cancel?.();

    const { dialog } = this.refs;
    if (dialog?.open) dialog.close();
    dialog?.classList.remove('dialog-closing');
    if (dialog) dialog.style.animation = '';

    this.#releaseBodyScrollLock();

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    super.disconnectedCallback();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #openAnimationFrame = null;
  #closeOperation = 0;
  #ownsBodyScrollLock = false;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#openAnimationFrame !== null) return;

    const scrollY = window.scrollY;
    this.#closeOperation += 1;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    this.#openAnimationFrame = requestAnimationFrame(() => {
      this.#openAnimationFrame = null;
      if (!this.isConnected || dialog.open) return;

      this.#acquireBodyScrollLock(scrollY);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    this.#cancelPendingOpen();
    this.#removeInteractionListeners();

    if (!dialog.open) {
      this.#releaseBodyScrollLock();
      return;
    }

    const closeOperation = ++this.#closeOperation;

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if (closeOperation !== this.#closeOperation || !dialog.open) return;

    this.#releaseBodyScrollLock();
    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  #cancelPendingOpen() {
    if (this.#openAnimationFrame === null) return;

    cancelAnimationFrame(this.#openAnimationFrame);
    this.#openAnimationFrame = null;
  }

  #removeInteractionListeners() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
  }

  #acquireBodyScrollLock(scrollY) {
    if (this.#ownsBodyScrollLock) return;

    if (bodyScrollLockOwners.size === 0) {
      bodyScrollLockState = {
        width: document.body.style.width,
        position: document.body.style.position,
        top: document.body.style.top,
        scrollY,
      };

      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
    }

    bodyScrollLockOwners.add(this);
    this.#ownsBodyScrollLock = true;
  }

  #releaseBodyScrollLock() {
    if (!this.#ownsBodyScrollLock) return;

    bodyScrollLockOwners.delete(this);
    this.#ownsBodyScrollLock = false;

    if (bodyScrollLockOwners.size > 0 || !bodyScrollLockState) return;

    const { width, position, top, scrollY } = bodyScrollLockState;
    bodyScrollLockState = null;

    document.body.style.width = width;
    document.body.style.position = position;
    document.body.style.top = top;
    window.scrollTo({ top: scrollY, behavior: 'instant' });
  }

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
